import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  Field,
  Metric,
  Select,
  formatDate,
} from "@odingard/design-system";
import type {
  RemediationAssignment,
  RemediationOverdueRunResult,
  RemediationStatus,
} from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api, regulatoryApi } from "../../lib/api";
import { GOVERNANCE_PERMISSIONS, GovPanel, GovernedAction } from "../../governance";
import {
  REMEDIATION_SOURCE_TYPES,
  REMEDIATION_STATUSES,
  RemediationStatusBadge,
  isOverdue,
  remediationSourceLabel,
  remediationStatusLabel,
} from "./regulatoryLabels";

interface Member {
  id: string;
  email: string;
  displayName: string;
}

type Due = "" | "overdue" | "week" | "none";

const NEXT_STATUS: Record<RemediationStatus, RemediationStatus[]> = {
  open: ["in_progress", "done", "cancelled"],
  in_progress: ["done", "cancelled"],
  done: [],
  cancelled: [],
};

// Remediation (APP-004 R3 P4): the work that comes out of a verified impact
// item or an audit finding, with the backend's own transition rules. The
// overdue sweep is a backend pass and chases an assignment once, so running it
// twice does not chase anything a second time.
export function GovRemediation() {
  const [params, setParams] = useSearchParams();
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const status = params.get("status") ?? "";
  const assignee = params.get("assignee") ?? "";
  const source = params.get("source") ?? "";
  const due = (params.get("due") ?? "") as Due;
  const [sweep, setSweep] = useState<RemediationOverdueRunResult | null>(null);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const assignments = useAsync<RemediationAssignment[]>(
    async () => (await regulatoryApi.assignments(orgId)).data.assignments,
    [orgId],
  );
  const members = useAsync<Member[]>(async () => (await api.members(orgId)).members, [orgId]);
  const memberName = (id: string) => {
    const match = (members.data ?? []).find((m) => m.id === id);
    return match ? match.displayName || match.email : id;
  };

  const withinWeek = (row: RemediationAssignment) => {
    if (row.dueAt === null) return false;
    const limit = new Date();
    limit.setDate(limit.getDate() + 7);
    return row.dueAt <= limit.toISOString().slice(0, 10);
  };

  const matches = (row: RemediationAssignment) => {
    if (status && row.status !== status) return false;
    if (assignee && row.assigneeId !== assignee) return false;
    if (source && row.sourceType !== source) return false;
    if (due === "overdue" && !isOverdue(row.status, row.dueAt)) return false;
    if (due === "week" && !withinWeek(row)) return false;
    if (due === "none" && row.dueAt !== null) return false;
    return true;
  };

  const rows = assignments.data ?? [];
  const open = rows.filter((r) => r.status === "open" || r.status === "in_progress").length;
  const overdue = rows.filter((r) => isOverdue(r.status, r.dueAt)).length;
  const done = rows.filter((r) => r.status === "done").length;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Remediation</h1>
          <p>
            The work raised from verified impact items and audit findings, with who owns it and
            when it is due. Status changes follow the rules the backend enforces: finished and
            cancelled work stays that way.{" "}
            <Link to="/studio/governance/impact">Impact assessments</Link>
          </p>
        </div>
        <div className="badge-row">
          <GovernedAction
            permission={GOVERNANCE_PERMISSIONS.regulationsManage}
            label="Run overdue sweep"
            title="Run the overdue sweep"
            description="Asks the backend to chase every assignment that is past its due date."
            consequence="An assignment is chased once. Running the sweep again reports zero for anything already chased."
            confirmLabel="Run sweep"
            busyLabel="Running…"
            triggerVariant="primary"
            onConfirm={async () => {
              const res = await regulatoryApi.runOverdue(orgId);
              setSweep(res.data.result);
              return res;
            }}
            onDone={assignments.reload}
          />
        </div>
      </div>

      {sweep ? (
        <Card>
          <CardHeader title="Last overdue sweep" />
          <CardBody>
            <div className="metric-grid">
              <div className="metric-card">
                <Metric value={sweep.overdue} label="Assignments chased" />
              </div>
            </div>
            <p className="muted-note">A repeat run reports zero for anything already chased.</p>
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardBody>
          <div className="metric-grid" data-testid="remediation-totals">
            <div className="metric-card">
              <Metric value={open} label="Open or in progress" />
            </div>
            <div className="metric-card">
              <Metric value={overdue} label="Past their due date" />
            </div>
            <div className="metric-card">
              <Metric value={done} label="Done" />
            </div>
          </div>
          <div className="filter-row">
            <Field label="Status">
              {(id) => (
                <Select id={id} value={status} onChange={(e) => setParam("status", e.target.value)}>
                  <option value="">Any status</option>
                  {REMEDIATION_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {remediationStatusLabel(s)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Owner">
              {(id) => (
                <Select id={id} value={assignee} onChange={(e) => setParam("assignee", e.target.value)}>
                  <option value="">Anyone</option>
                  {(members.data ?? []).map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.displayName || m.email}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Source">
              {(id) => (
                <Select id={id} value={source} onChange={(e) => setParam("source", e.target.value)}>
                  <option value="">Any source</option>
                  {REMEDIATION_SOURCE_TYPES.map((s) => (
                    <option key={s} value={s}>
                      {remediationSourceLabel(s)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Due">
              {(id) => (
                <Select id={id} value={due} onChange={(e) => setParam("due", e.target.value)}>
                  <option value="">Any date</option>
                  <option value="overdue">Past its due date</option>
                  <option value="week">Due within a week</option>
                  <option value="none">No due date</option>
                </Select>
              )}
            </Field>
          </div>
        </CardBody>
      </Card>

      <GovPanel
        title="Assignments"
        state={assignments}
        loadingLabel="Loading assignments…"
        isEmpty={(data) => data.filter(matches).length === 0}
        emptyTitle="No assignments match"
        emptyDescription="Clear the filters, or assign work from a verified impact item."
      >
        {(data) => (
          <DataTable
            caption="Remediation assignments"
            columns={[
              {
                key: "title",
                header: "Work",
                render: (row) => (
                  <>
                    <strong>{row.title}</strong>
                    <div className="badge-row">
                      <RemediationStatusBadge status={row.status} />
                      {isOverdue(row.status, row.dueAt) ? (
                        <Badge tone="danger">Past its due date</Badge>
                      ) : null}
                    </div>
                    {row.notes ? <div className="muted-note">{row.notes}</div> : null}
                  </>
                ),
              },
              {
                key: "source",
                header: "Raised from",
                render: (row) => (
                  <>
                    {remediationSourceLabel(row.sourceType)}
                    <div className="muted-note">
                      <code>{row.sourceId}</code>
                    </div>
                  </>
                ),
              },
              { key: "owner", header: "Owner", render: (row) => memberName(row.assigneeId) },
              {
                key: "due",
                header: "Due",
                render: (row) =>
                  row.dueAt ? formatDate(row.dueAt) : <span className="muted-note">No due date</span>,
              },
              {
                key: "workflow",
                header: "Tracked as",
                render: (row) => (
                  <div className="muted-note">
                    {row.lifecycleTaskId ? (
                      <div>
                        Lifecycle task <code>{row.lifecycleTaskId}</code>
                      </div>
                    ) : null}
                    {row.workflowInstanceId ? (
                      <div>
                        Workflow <code>{row.workflowInstanceId}</code>
                      </div>
                    ) : null}
                    {!row.lifecycleTaskId && !row.workflowInstanceId ? "Not tracked elsewhere" : null}
                    {row.completedAt ? <div>Completed {formatDate(row.completedAt)}</div> : null}
                  </div>
                ),
              },
              {
                key: "actions",
                header: "",
                render: (row) => (
                  <div className="badge-row">
                    {NEXT_STATUS[row.status].map((next) => (
                      <GovernedAction
                        key={next}
                        permission={GOVERNANCE_PERMISSIONS.regulationsManage}
                        label={remediationStatusLabel(next)}
                        title={`Move this work to ${remediationStatusLabel(next).toLowerCase()}`}
                        description="Records the change against the assignment."
                        consequence={
                          next === "done" || next === "cancelled"
                            ? "Finished and cancelled work cannot be moved again."
                            : undefined
                        }
                        confirmLabel="Confirm"
                        busyLabel="Saving…"
                        destructive={next === "cancelled"}
                        onConfirm={() => regulatoryApi.setAssignmentStatus(orgId, row.id, next)}
                        onDone={assignments.reload}
                      />
                    ))}
                  </div>
                ),
              },
            ]}
            rows={data.filter(matches)}
          />
        )}
      </GovPanel>
    </>
  );
}
